// 인증 + 프로필 상태 관리 훅 — USE_NEW_API 전환 지원
import { useState, useEffect, useCallback } from 'react'
// Supabase Session 제거 → 로컬 타입
type Session = { user: any; access_token: string; refresh_token: string } | null
import { tokenStorage } from '@/lib/apiClient'
import * as authApi from '@/lib/auth'
import type { AuthUser, UserProfile } from '@/types/auth'

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, _setSession] = useState<Session | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isProfileLoading] = useState(false)

  // ── USE_NEW_API=true: JWT 기반 ──────────────────────
  const loadMe = useCallback(async () => {
    try {
      const { user: me } = await authApi.getMe()
      // AuthUser(API) → Supabase User 호환 shape으로 변환
      const syntheticUser = {
        id: me.id,
        email: me.email,
        app_metadata: {},
        user_metadata: { full_name: me.fullName, avatar_url: me.avatarUrl },
        aud: 'authenticated',
        created_at: '',
      } as unknown as AuthUser
      const profile: UserProfile = {
        id: me.id,
        gallery_id: me.galleryId,
        role: me.role,
        full_name: me.fullName,
        avatar_url: me.avatarUrl,
        phone: null,
        is_active: true,
        created_at: '',
        updated_at: '',
      }
      setUser(syntheticUser)
      setUserProfile(profile)
      return true
    } catch {
      return false
    }
  }, [])

  useEffect(() => {
    const token = tokenStorage.getAccess()
    if (!token) {
      setIsLoading(false)
      return
    }
    loadMe().finally(() => setIsLoading(false))

    const onExpired = () => {
      setUser(null)
      setUserProfile(null)
    }
    window.addEventListener('canvly:auth-expired', onExpired)
    return () => window.removeEventListener('canvly:auth-expired', onExpired)
  }, [loadMe])

  const signIn = useCallback(async (email: string, password: string) => {
    const loginResult = await authApi.login({ email, password })
    await loadMe()
    return loginResult
  }, [loadMe])

  const signOut = useCallback(async () => {
    await authApi.logout()
    setUser(null)
    setUserProfile(null)
  }, [])

  return {
    user,
    session,
    userProfile,
    isLoading,
    isProfileLoading,
    signIn,
    signOut,
    role: userProfile?.role ?? null,
    galleryId: userProfile?.gallery_id ?? null,
    isAuthenticated: !!user,
    isAdmin: userProfile?.role === 'gallery_admin' || userProfile?.role === 'super_admin',
    isSuperAdmin: userProfile?.role === 'super_admin',
  }
}
